import React, { useEffect, useMemo, useState } from "react"

import { cloneDeep } from "lodash"
import { PlusIcon, Trash } from "lucide-react"
import { Input, Button, DefaultPopover, Separator } from "twyne-new.ui"

import { useResponsive, useUtilities } from "@/modules/cms/views/post/components/builder/context"

import { useCraftProp } from "../../utils"
import { useCustomKeyframe } from "../../utils/tailwindConfig/hooks"
import ApplyButtonPopover from "../components/ApplyButtonPopover"
import { TitleSandbox, SeparatorSandbox, SearchInput } from "../components/tailwindSandbox"

import AddPopover from "./AddPopover"
import AnimationWrapper from "./AnimationWrapper"
import Configuration from "./Configuration"
import ContextMenu from "./ContextMenu"
import List from "./List"
import { AnimationProvider, useAnimation } from "./context"

export default function AnimationProperty() {
  const { value: animation } = useCraftProp('animation')
  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()

  return <AnimationProvider key={device + selectedUtilities}>
    {animation && !Array.isArray(animation) ? null : <Animation />}
  </AnimationProvider>
}

function Animation() {
  const { value, setValue } = useCraftProp('animation')
  const { animationState, setAnimationState } = useAnimation()

  useEffect(() => {
    if (animationState === undefined) {
      setAnimationState(value || [{ name: '', duration: 1000, ease: 'ease', loops: 1 }])
    }
  }, [animationState, value, setAnimationState])

  const handleAdd = () => {
    const newAnimation = [...value || [], DEFAULT_VALUES]
    setValue(newAnimation)
    setAnimationState(newAnimation)
  }

  const handleDelete = index => {
    const newAnimation = value.filter((_, i) => i !== index)
    setValue(newAnimation)
    setAnimationState(newAnimation)
  }

  const handleApply = () => {
    setValue(animationState)
  }

  if (!animationState) {
    return null
  }

  return <>
    <div className='flex flex-col gap-2 mb-2'>
      {animationState?.map((_, index) => <PreviewItem
        key={index}
        onDelete={handleDelete}
        index={index}
        onApply={handleApply}
      />)}
    </div>
    <AddAnimationButton handleAdd={handleAdd} />
  </>
}

const PreviewItem = ({ index, onDelete, onApply }) => <>
  <PoppoverWrapper index={index} onApply={onApply}>
    <div className='flex gap-2 justify-between'>
      <div className='w-full'>
        <InputPreview index={index} />
      </div>
      <Button variant='light' size='sm' onClick={() => onDelete(index)}>
        <Trash size='14' />
      </Button>
    </div>
  </PoppoverWrapper>
</>

const InputPreview = ({ index }) => {
  const { value } = useCraftProp('animation')
  const animation = !value?.length ? [] : value
  const { name, duration } = animation[index] || {}

  const inputValue = useMemo(() => {
    const animationString = [name, (duration && name) && `duration: ${duration}ms`].filter(Boolean).join(', ')
    return animationString
  }, [name, duration])

  return <div className='flex items-center gap-1 relative'>
    <Input value={inputValue || ''} readOnly variant='accent' />
  </div>
}

const PoppoverWrapper = ({ children, index, onApply }) => {
  const { value: customKeyframes } = useCustomKeyframe()
  const { setAnimationState, animationState, animation: animationContext, animation: { search, items }, setAnimation: setAnimationContext, onCloseContextMenu } = useAnimation()

  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open && animationContext.search) {
      setAnimationContext({ ...animationContext, search: '' })
    }
  }, [open, setAnimationContext, animationContext])

  const handleClose = () => {
    setOpen(false)
    setAnimationState()
  }

  const handleChangeSearch = value => {
    setAnimationContext({ ...animationContext, search: value })
  }

  const customKeyframesList = useMemo(() => Object.entries(customKeyframes).map(([name, keyframe]) => ({ name, keyframe })), [customKeyframes])

  return <>
    <DefaultPopover
      trigger={children}
      triggerProps={{ asChild: true }}
      contentProps={{ className: 'w-[500px]', side: 'left', align: 'center' }}
      wrapperProps={{ open: open, onOpenChange: open => open ? setOpen(open) : handleClose() }}
    >
      <div className='relative'>
        <SearchInput title='Animation' value={search} onChange={handleChangeSearch} onClose={handleClose} />
        <Separator className='m-0 my-2' />
        <ContextMenu />
        <AddKeyframeButton />
        <div className='h-[500px]' onClick={() => onCloseContextMenu()}>
          <AnimationWrapper>
            <TitleSandbox>Predefined</TitleSandbox>
            {!items.length ? <div className='flex w-full justify-center h-[334px] mt-4'>No animation found</div> : <List items={items} index={index} className='h-[350px]' />}

            <SeparatorSandbox />

            <TitleSandbox>Custom</TitleSandbox>
            <List items={[{ category: '', items: customKeyframesList }]} index={index} custom />
          </AnimationWrapper>
        </div>
      </div>
      <Separator className='my-2' />
      <Configuration index={index} />
      <Separator className='my-2' />
      <ApplyButtonPopover
        onApply={onApply}
        onCancel={handleClose}
        name='animation'
        value={animationState}
      />
    </DefaultPopover>
  </>
}

function AddKeyframeButton() {
  const { value, setValue } = useCustomKeyframe()
  const { animation: { rightClick: { selected } } } = useAnimation()

  const handleAdd = item => {
    const copy = cloneDeep(value)
    selected && delete copy[`${selected.name}-copy`]
    setValue({ ...copy, [item.name]: item.keyframe })
  }

  return <AddPopover
    trigger={<Button className='w-fit absolute bottom-[18.5%] left-12 py-px' variant='light' size='sm'> <PlusIcon size='14' /> </Button>}
    onSubmit={handleAdd}
  />
}

const AddAnimationButton = ({ handleAdd }) => <>
  <div className='flex justify-end'>
    <Button variant='light' size='sm' onClick={() => handleAdd()}>
      <PlusIcon size='14' />
    </Button>
  </div>
</>

const DEFAULT_VALUES = { name: '', duration: 1000, ease: 'ease', loops: 1 }
